"use client";

import React, { useState, useEffect, useRef } from "react";
import { Menu, Search, User, X, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuHistory, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="25259" className="flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 animate-pulse rounded-sm bg-muted/20" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="hover:text-primary border-b-2 border-transparent hover:border-primary pb-1 text-xs font-black uppercase tracking-tighter text-foreground/90 transition-all duration-200">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 animate-pulse rounded-sm bg-muted/20" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-black uppercase tracking-tighter text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
function EcommerceProductsConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center p-8">
        <Loader className="h-6 w-6 animate-spin text-primary" />
      </div>;
  }
  if (error) {
    return <p className="p-4 text-sm text-destructive font-mono">Market data synchronization failed.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-sm text-muted-foreground font-mono">No matching assets found.</p>;
  }
  const visibleProducts = products.slice(0, 5);
  return <div className="flex flex-col divide-y divide-border">
      {visibleProducts.map((product, i) => <Link key={product.id} data-index={i} to={product.link} className="flex items-center gap-4 p-3 transition-colors hover:bg-primary/10" onClick={onClose}>
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-sm bg-muted">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center bg-secondary text-[10px] font-mono font-bold text-muted-foreground">
                ASSET
              </div>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-black uppercase tracking-tighter text-foreground">{product.title}</p>
            <div className="flex items-center gap-2 text-xs font-mono">
              {product.price !== undefined && <span className={product.discounted_price ? "text-muted-foreground/50 line-through" : "text-primary font-bold"}>
                  ${product.price}
                </span>}
              {product.discounted_price !== undefined && <span className="font-bold text-emerald-500">${product.discounted_price}</span>}
            </div>
          </div>
        </Link>)}
      {products.length > 5 && <div className="p-2 text-center">
          <span className="text-[10px] font-mono text-muted-foreground">And {products.length - 5} more assets...</span>
        </div>}
    </div>;
}
function SearchDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <div ref={containerRef} className="relative">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-foreground hover:bg-primary/20 hover:text-primary transition-colors" aria-label="Search Markets">
        <Search className="size-5" />
      </Button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-[320px] origin-top-right rounded-sm border border-primary/20 bg-card shadow-[0_10px_40px_rgba(0,0,0,0.8)] ring-1 ring-primary/10 sm:w-[400px]">
          <EcommerceProductsProvider wp_query={wp_query} postType="product">
            <div className="p-3">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="SEARCH MARKETS..." className="mb-2 font-mono" />
            </div>
            <div className="max-h-[60vh] overflow-y-auto border-t border-border bg-background">
              {!hasSearchQuery ? <p className="p-4 text-center text-xs font-mono text-muted-foreground">
                  Search for assets, pairs, or commodities...
                </p> : <EcommerceProductsConsumer onClose={() => setIsOpen(false)} />}
            </div>
          </EcommerceProductsProvider>
        </div>}
    </div>;
}
export default function MainSiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`dark fixed left-0 right-0 top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "border-b border-primary/30 bg-background/80 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,255,255,0.15)]" : "bg-background border-b border-white/5"}`}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        {}
        <div className="flex shrink-0 items-center">
          <Link to="/HomePage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604002/img/chibest_electrical_logo.png" alt="TradeSphere" className="h-7 w-auto object-contain brightness-0 invert" />
          </Link>
        </div>

        {}
        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="41" className="header-menu">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-2 md:gap-4">
          {}
          <SearchDropdown />

          {}
          <Link to="#" className="hidden items-center justify-center rounded-sm p-2 text-foreground transition-all hover:bg-primary/10 hover:text-primary hover:shadow-[0_0_15px_rgba(0,255,255,0.2)] md:flex" aria-label="Login">
            <User className="size-5" />
          </Link>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-primary/10 hover:text-primary" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="size-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex md:hidden">
          {}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="relative ml-auto h-full w-[300px] bg-card shadow-2xl border-l border-primary/20 flex flex-col">
            <div className="flex items-center justify-between border-b border-white/5 p-4">
              <span className="text-xs font-black uppercase tracking-tighter text-muted-foreground">Terminal Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-primary">
                <X className="size-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="41" className="mobile-menu">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="border-t border-white/5 p-4">
              <Link to="#" className="flex items-center gap-3 rounded-sm p-2 text-foreground hover:bg-primary/10 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                <User className="size-5" />
                <span className="text-sm font-black uppercase tracking-tighter">Client Portal</span>
              </Link>
            </div>
          </div>
        </div>}
    </section>;
}